import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  communitiesAPI, 
  startupsAPI, 
  eventsAPI, 
  jobsAPI,
  statsAPI,
  newsletterAPI
} from '../lib/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  Rocket, 
  Calendar, 
  Briefcase,
  Mail
} from 'lucide-react';

export const AdminPage = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('communities');
  
  // Data states
  const [communities, setCommunities] = useState([]);
  const [startups, setStartups] = useState([]);
  const [events, setEvents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/login');
    }
  }, [isAdmin, authLoading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const statsRes = await statsAPI.get();
      setStats(statsRes.data);

      switch (activeTab) {
        case 'communities':
          const commRes = await communitiesAPI.getAll();
          setCommunities(commRes.data);
          break;
        case 'startups':
          const startRes = await startupsAPI.getAll();
          setStartups(startRes.data);
          break;
        case 'events':
          const eventsRes = await eventsAPI.getAll();
          setEvents(eventsRes.data);
          break;
        case 'jobs':
          const jobsRes = await jobsAPI.getAll();
          setJobs(jobsRes.data);
          break;
        case 'newsletter':
          const subsRes = await newsletterAPI.getSubscribers();
          setSubscribers(subsRes.data);
          break;
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      switch (type) {
        case 'community':
          await communitiesAPI.delete(id);
          setCommunities(communities.filter(c => c.id !== id));
          break;
        case 'startup':
          await startupsAPI.delete(id);
          setStartups(startups.filter(s => s.id !== id));
          break;
        case 'event':
          await eventsAPI.delete(id);
          setEvents(events.filter(e => e.id !== id));
          break;
        case 'job':
          await jobsAPI.delete(id);
          setJobs(jobs.filter(j => j.id !== id));
          break;
      }
      toast.success('Item deleted successfully');
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  if (authLoading || !isAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-8">
          <h1 className="text-heading text-3xl mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Manage the Portland Tech Ecosystem directory
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-muted/30">
        <div className="container-wide py-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="font-bold">{stats.communities || 0}</div>
                <div className="text-xs text-muted-foreground">Communities</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Rocket className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="font-bold">{stats.startups || 0}</div>
                <div className="text-xs text-muted-foreground">Startups</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="font-bold">{stats.events || 0}</div>
                <div className="text-xs text-muted-foreground">Events</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Briefcase className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="font-bold">{stats.jobs || 0}</div>
                <div className="text-xs text-muted-foreground">Jobs</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="font-bold">{stats.subscribers || 0}</div>
                <div className="text-xs text-muted-foreground">Subscribers</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section>
        <div className="container-wide py-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between mb-6">
              <TabsList>
                <TabsTrigger value="communities" data-testid="admin-communities-tab">Communities</TabsTrigger>
                <TabsTrigger value="startups" data-testid="admin-startups-tab">Startups</TabsTrigger>
                <TabsTrigger value="events" data-testid="admin-events-tab">Events</TabsTrigger>
                <TabsTrigger value="jobs" data-testid="admin-jobs-tab">Jobs</TabsTrigger>
                <TabsTrigger value="newsletter" data-testid="admin-newsletter-tab">Newsletter</TabsTrigger>
              </TabsList>
              
              {activeTab !== 'newsletter' && (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setEditItem(null)} data-testid="add-new-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add New
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editItem ? 'Edit' : 'Add'} {activeTab.slice(0, -1)}
                      </DialogTitle>
                    </DialogHeader>
                    <AdminForm 
                      type={activeTab} 
                      item={editItem}
                      onSuccess={() => {
                        setDialogOpen(false);
                        fetchData();
                      }}
                    />
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <TabsContent value="communities">
              <AdminTable
                data={communities}
                columns={['name', 'category', 'member_count']}
                onEdit={(item) => { setEditItem(item); setDialogOpen(true); }}
                onDelete={(id) => handleDelete('community', id)}
                loading={loading}
              />
            </TabsContent>

            <TabsContent value="startups">
              <AdminTable
                data={startups}
                columns={['name', 'industry', 'stage']}
                onEdit={(item) => { setEditItem(item); setDialogOpen(true); }}
                onDelete={(id) => handleDelete('startup', id)}
                loading={loading}
              />
            </TabsContent>

            <TabsContent value="events">
              <AdminTable
                data={events}
                columns={['title', 'event_type', 'date']}
                onEdit={(item) => { setEditItem(item); setDialogOpen(true); }}
                onDelete={(id) => handleDelete('event', id)}
                loading={loading}
              />
            </TabsContent>

            <TabsContent value="jobs">
              <AdminTable
                data={jobs}
                columns={['title', 'company', 'job_type']}
                onEdit={(item) => { setEditItem(item); setDialogOpen(true); }}
                onDelete={(id) => handleDelete('job', id)}
                loading={loading}
              />
            </TabsContent>

            <TabsContent value="newsletter">
              <Card>
                <CardHeader>
                  <CardTitle>Newsletter Subscribers ({subscribers.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="divide-y divide-border">
                    {subscribers.map(sub => (
                      <div key={sub.id} className="py-3 flex items-center justify-between">
                        <span>{sub.email}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(sub.subscribed_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                    {subscribers.length === 0 && (
                      <p className="text-muted-foreground py-4 text-center">No subscribers yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

// Admin Table Component
const AdminTable = ({ data, columns, onEdit, onDelete, loading }) => {
  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No items found. Click "Add New" to create one.
      </div>
    );
  }

  return (
    <div className="border border-border rounded-sm overflow-hidden">
      <table className="w-full">
        <thead className="bg-muted">
          <tr>
            {columns.map(col => (
              <th key={col} className="text-left p-4 text-sm font-medium capitalize">
                {col.replace('_', ' ')}
              </th>
            ))}
            <th className="text-right p-4 text-sm font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.map(item => (
            <tr key={item.id} className="hover:bg-muted/50">
              {columns.map(col => (
                <td key={col} className="p-4 text-sm">
                  {item[col] || '—'}
                </td>
              ))}
              <td className="p-4 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(item)}
                  data-testid={`edit-${item.id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(item.id)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-${item.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Admin Form Component
const AdminForm = ({ type, item, onSuccess }) => {
  const [formData, setFormData] = useState(item || {});
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      switch (type) {
        case 'communities':
          if (item) {
            await communitiesAPI.update(item.id, formData);
          } else {
            await communitiesAPI.create(formData);
          }
          break;
        case 'startups':
          if (item) {
            await startupsAPI.update(item.id, formData);
          } else {
            await startupsAPI.create(formData);
          }
          break;
        case 'events':
          if (item) {
            await eventsAPI.update(item.id, formData);
          } else {
            await eventsAPI.create(formData);
          }
          break;
        case 'jobs':
          if (item) {
            await jobsAPI.update(item.id, formData);
          } else {
            await jobsAPI.create(formData);
          }
          break;
      }
      toast.success(item ? 'Updated successfully' : 'Created successfully');
      onSuccess();
    } catch (err) {
      toast.error('Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const renderFields = () => {
    switch (type) {
      case 'communities':
        return (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">Name *</label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Category *</label>
              <Select
                value={formData.category || ''}
                onValueChange={(val) => setFormData({ ...formData, category: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ai-ml">AI & Machine Learning</SelectItem>
                  <SelectItem value="robotics">Robotics & Hardware</SelectItem>
                  <SelectItem value="creative">Creative Tech</SelectItem>
                  <SelectItem value="startup">Startup Networks</SelectItem>
                  <SelectItem value="diversity">Diversity & Inclusion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Website</label>
              <Input
                value={formData.website || ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Member Count</label>
              <Input
                type="number"
                value={formData.member_count || ''}
                onChange={(e) => setFormData({ ...formData, member_count: parseInt(e.target.value) })}
              />
            </div>
          </>
        );
      
      case 'startups':
        return (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">Name *</label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Industry *</label>
                <Select
                  value={formData.industry || ''}
                  onValueChange={(val) => setFormData({ ...formData, industry: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ai">AI & Automation</SelectItem>
                    <SelectItem value="robotics">Robotics</SelectItem>
                    <SelectItem value="saas">SaaS & Software</SelectItem>
                    <SelectItem value="healthtech">Health Tech</SelectItem>
                    <SelectItem value="cleantech">Clean Tech</SelectItem>
                    <SelectItem value="creative">Creative Tech</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Stage *</label>
                <Select
                  value={formData.stage || ''}
                  onValueChange={(val) => setFormData({ ...formData, stage: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seed">Seed</SelectItem>
                    <SelectItem value="series-a">Series A</SelectItem>
                    <SelectItem value="series-b">Series B</SelectItem>
                    <SelectItem value="growth">Growth</SelectItem>
                    <SelectItem value="acquired">Acquired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Website</label>
              <Input
                value={formData.website || ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
            </div>
          </>
        );
      
      case 'events':
        return (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">Title *</label>
              <Input
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Event Type *</label>
                <Select
                  value={formData.event_type || ''}
                  onValueChange={(val) => setFormData({ ...formData, event_type: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meetup">Meetup</SelectItem>
                    <SelectItem value="conference">Conference</SelectItem>
                    <SelectItem value="hackathon">Hackathon</SelectItem>
                    <SelectItem value="workshop">Workshop</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Date *</label>
                <Input
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Location *</label>
              <Input
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              />
            </div>
          </>
        );
      
      case 'jobs':
        return (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">Title *</label>
              <Input
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Company *</label>
              <Input
                value={formData.company || ''}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Job Type *</label>
                <Select
                  value={formData.job_type || ''}
                  onValueChange={(val) => setFormData({ ...formData, job_type: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time">Full-time</SelectItem>
                    <SelectItem value="part-time">Part-time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Experience *</label>
                <Select
                  value={formData.experience_level || ''}
                  onValueChange={(val) => setFormData({ ...formData, experience_level: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entry">Entry Level</SelectItem>
                    <SelectItem value="mid">Mid Level</SelectItem>
                    <SelectItem value="senior">Senior</SelectItem>
                    <SelectItem value="lead">Lead / Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Location</label>
              <Input
                value={formData.location || 'Portland, OR'}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </div>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {renderFields()}
      <div className="flex justify-end gap-3 pt-4">
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : (item ? 'Update' : 'Create')}
        </Button>
      </div>
    </form>
  );
};
